<template>
  <ValidationProvider
    v-slot="{ errors }"
    :name="name"
    tag="div"
    :rules="required ? 'required' : ''"
  >
    <div
      v-if="label"
      :id="`${id}-label`"
      :class="[visuallyHiddenLabel ? 'sr-only' : 'label']"
    >
      {{ label }}
    </div>
    <input
      :id="`${id}-input`"
      :aria-labelledby="label ? `${id}-label` : undefined"
      class="sr-only"
      disabled
      :name="name"
      :value="value"
      :required="required"
      type="text"
    />
    <div class="component-wrapper">
      <div
        :id="id"
        :class="[
          'component',
          isExpanded ? 'component--expanded' : 'component--collapsed'
        ]"
        :style="{ '--combobox-highlight-index': highlightIndex }"
        @mouseout="handleMouseOut"
        @mouseover="handleMouseOver"
      >
        <div
          :id="`${id}-combobox`"
          :aria-activedescendant="getOptionId(activeOption)"
          :aria-controls="`${id}-listbox`"
          :aria-expanded="isExpanded"
          aria-haspopup="listbox"
          :aria-labelledby="label ? `${id}-label` : undefined"
          :aria-readonly="isReadonly"
          :class="['combobox', { active: highlightIndex === 0 }]"
          role="combobox"
          tabindex="0"
          @focusout="handleFocusout"
          @click="handleClick"
          @keydown="handleKeydown"
        >
          <span class="combobox__value">
            {{ selectedOption?.key || placeholder }}
          </span>
          <fa
            v-if="!isReadonly"
            icon="chevron-down"
            aria-hidden="true"
            class="combobox__icon"
          />
        </div>
        {{/*
          `transition-behavior` is inlined below because it may otherwise be
          stripped during build as an unknown property. We can remove this
          removed once we've updated Vue/Nuxt and PostCSS
        */}}
        <div
          class="listbox-wrapper"
          :hidden="!isExpanded"
          style="transition-behavior: allow-discrete;"
        >
          <div
            :id="`${id}-listbox`"
            :aria-labelledby="label ? `${id}-label` : undefined"
            class="listbox"
            role="listbox"
            tabindex="-1"
            @click="handleClick"
          >
            <div
              v-for="(option, index) in options"
              :id="getOptionId(option)"
              :key="getOptionId(option)"
              :aria-selected="option.value === value"
              :class="['option', { active: highlightIndex === index }]"
              :data-value="option.value"
              role="option"
            >
              {{ option.key }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <fade appear>
      <ul v-if="errors && Object.values(errors).flat().length">
        <li
          v-for="(error, i) in Object.values(errors).flat()"
          :key="`error-${i}`"
          class="text-xs opacity-75 pt-1/4em"
        >
          {{ error }}
        </li>
      </ul>
    </fade>
  </ValidationProvider>
</template>

<script>
import { closest, contains } from '~/util/dom'
import { slugify, uniqueId } from '~/util/helpers'

export default {
  name: 'SelectDropdown',

  props: {
    label: {
      default: null,
      type: String
    },
    /**
     * When true, the provided label is visually hidden
     */
    visuallyHiddenLabel: {
      default: false,
      type: Boolean
    },
    /**
     * @type {{ key: string; value: string; }[]}
     */
    options: {
      default: () => [],
      type: Array,
      required: true
    },
    value: {
      default: null,
      type: String
    },
    placeholder: {
      default: 'select',
      type: String
    },
    disabled: {
      default: false,
      type: Boolean
    },
    required: {
      default: false,
      type: Boolean
    },
    name: {
      default: undefined,
      type: String
    }
  },

  data() {
    return {
      highlightIndex: -1,
      activeOption: undefined,
      id: uniqueId(),
      isExpanded: false
    }
  },

  computed: {
    /**
     * When there are no additional options to show, flag the component as
     * readonly and preempt user interaction.
     */
    isReadonly() {
      return (
        this.options.length < 1 ||
        (this.options.length === 1 && this.options[0].value === this.value)
      )
    },
    selectedOption() {
      return this.options.find(o => o.value === this.value)
    }
  },

  watch: {
    activeOption(next) {
      if (next) {
        this.highlightIndex = this.options.indexOf(next)
      }
    },
    isExpanded(next) {
      if (next) {
        this.activeOption = undefined
      }
    }
  },

  methods: {
    getOptionId(option) {
      if (option && typeof option === 'object' && option.value) {
        return slugify(`${this.id}-option-${option.value}`)
      }
      return undefined
    },
    /** @param {PointerEvent} event */
    handleClick(event) {
      if (this.isReadonly) {
        return
      }

      event.preventDefault()

      if (closest(event.target, '[role="combobox"]')) {
        this.isExpanded = !this.isExpanded
        return
      }

      const option = closest(event.target, '[role="option"]')

      if (option) {
        this.$emit('input', option.dataset.value)
        this.isExpanded = false
      }
    },
    /** @param {FocusEvent} event */
    handleFocusout(event) {
      if (!contains(document.getElementById(this.id), event.relatedTarget)) {
        this.isExpanded = false
      }
    },
    /**
     * For keyboard functionality requirements see the WAI ARIA APG
     * {@link https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/ | select-only combobox example}
     *
     * @param {KeyboardEvent} event
     */
    handleKeydown(event) {
      if (this.isReadonly) {
        return
      }

      const activeOptionIndex = this.options.indexOf(
        this.activeOption || this.options.find(o => o.value === this.value)
      )

      const getOptionByIndex = n => {
        const index =
          n < 0 ? 0 : n >= this.options.length ? this.options.length - 1 : n
        return this.options[index]
      }

      if (!this.isExpanded) {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          this.isExpanded = true
        }
        if (event.key === 'ArrowUp' || event.key === 'Home') {
          event.preventDefault()
          this.isExpanded = true
          this.activeOption = this.options[0]
        }
        if (event.key === 'End') {
          event.preventDefault()
          this.isExpanded = true
          this.activeOption = this.options[this.options.length - 1]
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          this.isExpanded = true
        }
        if (event.key === 'Home') {
          event.preventDefault()
          this.isExpanded = true
          this.activeOption = this.options[0]
        }
        return
      }

      if (this.isExpanded) {
        if (event.key === 'ArrowDown') {
          event.preventDefault()
          this.activeOption = getOptionByIndex(activeOptionIndex + 1)
        }
        if (event.key === 'ArrowUp') {
          event.preventDefault()
          if (event.altKey) {
            if (this.activeOption) {
              this.$emit('input', this.activeOption.value)
            }
            this.isExpanded = false
          } else {
            this.activeOption = getOptionByIndex(activeOptionIndex - 1)
          }
        }
        if (event.key === 'End') {
          event.preventDefault()
          this.activeOption = getOptionByIndex(this.options.length)
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          if (this.activeOption) {
            this.$emit('input', this.activeOption.value)
          }
          this.isExpanded = false
        }
        if (event.key === 'Escape') {
          event.preventDefault()
          this.isExpanded = false
        }
        if (event.key === 'Home') {
          event.preventDefault()
          this.activeOption = getOptionByIndex(0)
        }
        if (event.key === 'PageDown') {
          event.preventDefault()
          this.activeOption = getOptionByIndex(activeOptionIndex + 10)
        }
        if (event.key === 'PageUp') {
          event.preventDefault()
          this.activeOption = getOptionByIndex(activeOptionIndex - 10)
        }
        if (event.key === 'Tab') {
          if (this.activeOption) {
            this.$emit('input', this.activeOption.value)
          }
        }
      }
    },
    handleMouseOut() {
      this.highlightIndex = -1
    },
    /** @param {MouseEvent} event */
    handleMouseOver(event) {
      if (closest(event.target, '[role="combobox"]')) {
        this.highlightIndex = 0
        return
      }
      const option = closest(event.target, '[role="option"]')
      if (option) {
        this.highlightIndex = this.options.findIndex(
          o => o.value === option.dataset.value
        )
      }
    }
  }
}
</script>

<style scoped>
.label {
  @apply font-medium;
  margin-block-end: var(--input-gutter-sm);
  padding-inline-start: var(--input-gutter);
}

.component-wrapper {
  --combobox-size-block: calc(
    1lh + 2 * var(--input-border-width) + 2 * var(--input-gutter)
  );

  block-size: var(--combobox-size-block);
  border-radius: calc(0.5 * var(--combobox-size-block));
  line-height: theme('lineHeight.tight');
  position: relative;
  z-index: 2;
}

.component-wrapper:has([role='combobox'][aria-readonly='true']) {
  pointer-events: none;
  z-index: auto;
}

.component-wrapper:has(:focus-visible, [data-focus-visible-added]) {
  outline: 3px solid var(--input-text-color);
  outline-offset: 4px;
}

.component-wrapper :where(:focus-visible, [data-focus-visible-added]) {
  outline: none;
}

.component {
  align-items: start;
  border-color: var(--input-text-color);
  border-radius: calc(0.5 * var(--combobox-size-block));
  border-style: solid;
  border-width: var(--input-border-width);
  clip-path: inset(
    calc(-1 * var(--input-border-width)) calc(-1 * var(--input-border-width))
      round var(--input-border-radius)
  );
  color: var(--input-text-color);
  display: grid;
  grid-template: 1fr / minmax(0 1fr);
  overflow: hidden;
  position: relative;
  transition: var(--hover-speed);
  transition-property: color, clip-path, background-color;
}

.component--expanded {
  background-color: var(--input-text-color);
  color: var(--input-background-color);
}

.component > * {
  grid-column: 1;
  grid-row: 1;
  inline-size: 100%;
}

.listbox-wrapper {
  display: grid;
  grid-template: 1fr / 1fr;
  overflow: hidden;
  position: relative;
  transition: var(--hover-speed);
  transition-behavior: allow-discrete;
  transition-property: display grid-template-rows transform;
}

.component--collapsed .listbox-wrapper {
  display: none;
  grid-template-rows: 0fr;
  transform: translateY(var(--combobox-size-block));
}

@starting-style {
  .component--expanded .listbox-wrapper {
    grid-template-rows: 0fr;
    transform: translateY(var(--combobox-size-block));
  }
}

.listbox-wrapper > * {
  inline-size: 100%;
  min-block-size: 0;
}

.combobox {
  align-items: center;
  display: flex;
  gap: 1em;
  inline-size: 100%;
  justify-content: space-between;
}

.combobox,
.option {
  cursor: pointer;
  inline-size: 100%;
  max-inline-size: 100%;
  padding: var(--input-gutter);
  position: relative;
  transition: var(--hover-speed);
  transition-property: color, background-color;
}

.combobox.active,
.option.active {
  background-color: var(--input-background-color);
  color: var(--input-text-color);
}

.combobox__value,
.option {
  text-overflow: ellipsis;
  user-select: none;
  white-space: nowrap;
}

.combobox__value,
.combobox__icon {
  transition: transform var(--hover-speed);
}

.component--expanded .combobox__value {
  transform: translateY(calc(-1 * var(--combobox-size-block)));
}

.combobox__icon {
  flex: 0 0 auto;
  pointer-events: none;
  position: relative;
  z-index: 1;
}

.component--expanded .combobox__icon {
  transform: scale(1, -1);
}

.option::after {
  background-color: var(--input-background-color);
  block-size: var(--input-border-width);
  content: '';
  inline-size: calc(100% - 2 * var(--input-gutter));
  inset-block-end: calc(-1 * var(--input-border-width));
  inset-inline-start: var(--input-gutter);
  position: absolute;
  transition: var(--hover-speed);
  transition-property: opcaity, transform;
  z-index: 1;
}

.component--collapsed .option::after {
  transform: scaleX(0);
}

@starting-style {
  .option::after {
    transform: scaleX(0);
  }
}

.option.active::after {
  opacity: 0;
}

.option:nth-child(1)::after {
  transition-delay: calc(0.7 * var(--hover-speed));
}

.option:nth-child(2)::after {
  transition-delay: calc(1.25 * var(--hover-speed));
  transition-duration: calc(0.95 * var(--hover-speed));
}

.option:nth-child(3)::after {
  transition-delay: calc(1.55 * var(--hover-speed));
  transition-duration: calc(0.9 * var(--hover-speed));
}

.option:nth-child(4)::after {
  transition-delay: calc(1.75 * var(--hover-speed));
  transition-duration: calc(0.8 * var(--hover-speed));
}

.option:nth-child(n + 5)::after {
  transition-delay: calc(1.9 * var(--hover-speed));
  transition-duration: calc(0.55 * var(--hover-speed));
}
</style>
